import React, { useState, useEffect } from "react";
import { MdAccountCircle, MdHome } from "react-icons/md"; // Keep MdAccountCircle for fallback
import { LogIn } from "lucide-react"; // Import Lucide Login icon
import styled from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "./images/logo.png";
import { FaPlane, FaBed, FaArrowLeft } from "react-icons/fa";
import { useAuth } from "./AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../Config/config";
import avatar1 from "../Avatars/1.jpg";
import avatar2 from "../Avatars/2.jpg";
import avatar3 from "../Avatars/3.jpg";
import avatar4 from "../Avatars/4.jpg";

const Navbar = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [userAvatar, setUserAvatar] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchUserAvatar = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserAvatar(userData.avatarSelected); // Set the selected avatar
          }
        } catch (error) {
          console.error("Error fetching user avatar:", error);
        }
      }
    };

    fetchUserAvatar();
  }, [user]);

  const handleBackToPackageDetails = () => {
    const packageDetails = location.state?.packageDetails;

    if (packageDetails?.id) {
      navigate(`/packagedetails/${packageDetails.id}`);
    } else {
      const fallbackId = sessionStorage.getItem("currentPackageId");
      if (fallbackId) {
        navigate(`/packagedetails/${fallbackId}`);
      } else {
        navigate("/packages");
      }
    }
  };

  const handleCenterButtonClick = () => {
    if (isMobile) {
      if (location.pathname === "/account" || location.pathname === "/login") {
        navigate("/");
      } else if (location.pathname.startsWith("/packagedetails/")) {
        navigate("/packages");
      } else if (location.pathname.startsWith("/hotel/")) {
        navigate("/hotels");
      } else if (location.pathname === "/purchase") {
        handleBackToPackageDetails();
      } else if (location.pathname === "/details") {
        navigate("/packages");
      } else {
        if (location.pathname === "/") {
          navigate("/hotels");
        } else if (location.pathname === "/hotels") {
          navigate("/");
        }
      }
    }
  };

  const handleAccountClick = () => {
    if (user) {
      navigate("/account");
    } else {
      navigate("/login");
    }
  };

  const getAvatarImage = (avatarNumber) => {
    switch (avatarNumber) {
      case "1":
        return avatar1;
      case "2":
        return avatar2;
      case "3":
        return avatar3;
      case "4":
        return avatar4;
      default:
        return null; // Return null if no avatar is selected
    }
  };

  const isAccountPage = location.pathname === "/account";
  const isLoginPage = location.pathname === "/login";
  const isPackageDetailsPage = location.pathname.startsWith("/packagedetails/");
  const isPurchasePage = location.pathname === "/purchase";
  const isHotelDetailsPage = location.pathname.startsWith("/hotel/");
  const isDetailsPage = location.pathname === "/details";

  const shouldShowMobileNavBar = !(isDetailsPage || isPurchasePage);

  const getButtonIcon = () => {
    if (isAccountPage || isLoginPage) return <StyledMdHome />;
    if (isPackageDetailsPage || isPurchasePage || isDetailsPage)
      return <StyledFaArrowLeft />;
    if (isHotelDetailsPage) return <StyledFaArrowLeft />;
    return location.pathname === "/hotels" ? (
      <StyledFaPlane />
    ) : (
      <StyledFaBed />
    );
  };

  const getButtonText = () => {
    if (isAccountPage || isLoginPage) return "Home";
    if (isPackageDetailsPage) return "Packages";
    if (isPurchasePage) return "Package Details";
    if (isHotelDetailsPage) return "Hotels";
    if (isDetailsPage) return "Packages";
    return location.pathname === "/hotels" ? "View Packages" : "Explore Hotels";
  };

  return (
    <>
      <NavContainer isMobile={isMobile}>
        <LogoWrapper isMobile={isMobile}>
          <LogoImage
            src={logo}
            alt="Club Njoy Logo"
            onClick={() => navigate("/")}
          />
          {isMobile && (
            <IconContainer onClick={handleAccountClick}>
              {user ? (
                getAvatarImage(userAvatar) ? (
                  <AvatarImage
                    src={getAvatarImage(userAvatar)}
                    alt="User Avatar"
                  />
                ) : (
                  <StyledMdAccountCircle /> // Fallback to MdAccountCircle if avatar is missing
                )
              ) : (
                <StyledLogIn /> // Show Lucide Login icon for unauthenticated users
              )}
            </IconContainer>
          )}
        </LogoWrapper>
        {!isMobile && (
          <NavItems>
            <NavItem to="/" active={location.pathname === "/"}>
              HOME
            </NavItem>
            <NavItem to="/about" active={location.pathname === "/about"}>
              ABOUT US
            </NavItem>
            <NavItem to="/contact" active={location.pathname === "/contact"}>
              CONTACT US
            </NavItem>
            <NavItem to="/terms" active={location.pathname === "/terms"}>
              T & C
            </NavItem>
            <NavItem to="/privacy" active={location.pathname === "/privacy"}>
              PRIVACY POLICY
            </NavItem>
          </NavItems>
        )}
        {!isMobile && (
          <IconContainer onClick={handleAccountClick}>
            {user ? (
              getAvatarImage(userAvatar) ? (
                <AvatarImage
                  src={getAvatarImage(userAvatar)}
                  alt="User Avatar"
                />
              ) : (
                <StyledMdAccountCircle /> // Fallback to MdAccountCircle if avatar is missing
              )
            ) : (
              <StyledLogIn /> // Show Lucide Login icon for unauthenticated users
            )}
          </IconContainer>
        )}
      </NavContainer>
      {isMobile && shouldShowMobileNavBar && (
        <MobileNavBar>
          <CenterMobileNavItem as="button" onClick={handleCenterButtonClick}>
            <IconWrapper>{getButtonIcon()}</IconWrapper>
            <ButtonText>{getButtonText()}</ButtonText>
          </CenterMobileNavItem>
        </MobileNavBar>
      )}
    </>
  );
};

// Styled Components (unchanged)
const NavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 30px;
  position: fixed;
  top: ${(props) => (props.isMobile ? "0" : "35px")};
  left: ${(props) => (props.isMobile ? "0" : "50%")};
  transform: ${(props) => (props.isMobile ? "none" : "translateX(-50%)")};
  z-index: 1000;
  background-color: ${(props) => (props.isMobile ? "transparent" : "white")};
  box-shadow: ${(props) =>
    props.isMobile
      ? "none"
      : "0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 5px rgba(0, 0, 0, 0.1)"};
  border-radius: ${(props) => (props.isMobile ? "0" : "50px")};
  width: ${(props) => (props.isMobile ? "100%" : "80%")};
  max-width: ${(props) => (props.isMobile ? "none" : "1200px")};

  @media (max-width: 768px) {
    padding: 10px 15px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
  }

  @media (max-width: 320px) {
    padding: 5px 8px;
  }
`;

const LogoWrapper = styled.div`
  background-color: ${(props) => (props.isMobile ? "white" : "transparent")};
  border-radius: 16px;
  height: ${(props) => (props.isMobile ? "12vh" : "100%")};
  padding: ${(props) => (props.isMobile ? "15px" : "0")};
  width: ${(props) => (props.isMobile ? "100%" : "auto")};
  max-width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 480px) {
    height: ${(props) => (props.isMobile ? "10vh" : "100%")};
    padding: ${(props) => (props.isMobile ? "10px" : "0")};
  }

  @media (max-width: 320px) {
    height: ${(props) => (props.isMobile ? "8vh" : "100%")};
    padding: ${(props) => (props.isMobile ? "8px" : "0")};
  }
`;

const LogoImage = styled.img`
  height: ${(props) => (props.isMobile ? "3.8rem" : "calc(5rem - 10px)")};
  width: auto;
  padding: ${(props) => (props.isMobile ? "0" : "5px 0")};
  cursor: pointer; 
  
  @media (max-width: 480px) {
    height: ${(props) => (props.isMobile ? "3rem" : "calc(4rem - 10px)")};
  }

  @media (max-width: 320px) {
    height: ${(props) => (props.isMobile ? "2.5rem" : "calc(3.5rem - 10px)")};
  }
`;

const NavItems = styled.div`
  display: flex;
  font-size: 26px;
  font-weight: 600;
  justify-content: center;
  gap: 40px;

  @media (max-width: 768px) {
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 15px;
  }

  @media (max-width: 320px) {
    gap: 10px;
  }
`;

const NavItem = styled(Link)`
  color: ${(props) => (props.active ? "#441B77" : "#333")};
  text-decoration: none;
  font-size: 14px;
  font-family: "Poppins", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 480px) {
    font-size: 12px;
  }

  @media (max-width: 320px) {
    font-size: 11px;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;

  @media (max-width: 480px) {
    gap: 10px;
  }

  @media (max-width: 320px) {
    gap: 8px;
  }
`;

const StyledMdAccountCircle = styled(MdAccountCircle)`
  font-size: 40px;
  color: #441B77;

  @media (max-width: 480px) {
    font-size: 32px;
  }

  @media (max-width: 320px) {
    font-size: 28px;
  }
`;

const StyledLogIn = styled(LogIn)`
  font-size: 40px;
  color: #441B77;

  @media (max-width: 480px) {
    font-size: 32px;
  }

  @media (max-width: 320px) {
    font-size: 28px;
  }
`;

const AvatarImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }

  @media (max-width: 320px) {
    width: 28px;
    height: 28px;
  }
`;

const MobileNavBar = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  height: 70px;
  background-color: #441B77;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  border-radius: 35px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 480px) {
    width: 95%;
    height: 65px;
    bottom: 15px;
  }

  @media (max-width: 320px) {
    width: 98%;
    height: 60px;
    bottom: 10px;
    border-radius: 30px;
  }
`;

const CenterMobileNavItem = styled.button`
  background-color: transparent;
  border: none;
  padding: 0 25px;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;

  @media (max-width: 480px) {
    padding: 0 20px;
  }

  @media (max-width: 320px) {
    padding: 0 15px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;

  @media (max-width: 480px) {
    width: 45px;
    height: 45px;
  }

  @media (max-width: 320px) {
    width: 40px;
    height: 40px;
  }
`;

const StyledFaPlane = styled(FaPlane)`
  font-size: 32px;
  color: white;

  @media (max-width: 480px) {
    font-size: 28px;
  }

  @media (max-width: 320px) {
    font-size: 24px;
  }
`;

const StyledFaBed = styled(FaBed)`
  font-size: 32px;
  color: white;

  @media (max-width: 480px) {
    font-size: 28px;
  }

  @media (max-width: 320px) {
    font-size: 24px;
  }
`;

const StyledMdHome = styled(MdHome)`
  font-size: 32px;
  color: white;

  @media (max-width: 480px) {
    font-size: 28px;
  }

  @media (max-width: 320px) {
    font-size: 24px;
  }
`;

const StyledFaArrowLeft = styled(FaArrowLeft)`
  font-size: 32px;
  color: white;

  @media (max-width: 480px) {
    font-size: 28px;
  }

  @media (max-width: 320px) {
    font-size: 24px;
  }
`;

const ButtonText = styled.span`
  font-size: 18px;
  font-weight: 600;
  font-family: "Poppins", sans-serif;
  margin-top: 4px;

  @media (max-width: 480px) {
    font-size: 16px;
  }

  @media (max-width: 320px) {
    font-size: 14px;
  }
`;

export default Navbar;